// src/modules/time-tracking/TimeTrackingSettings.js (admin)
//
// Admin-set defaults for the time-tracking area. Stored in the shared Settings
// k/v store (key `time_tracking_prefs`) and consumed as form defaults by
// SiteForm (radius) and JobForm (auto-create-site + radius).
import React, { useState, useEffect } from 'react';
import { FaCog, FaRulerHorizontal, FaSave, FaSpinner } from 'react-icons/fa';
import DashboardLayout from '../../layouts/DashboardLayout';
import Input from '../../components/shared/Input';
import Checkbox from '../../components/shared/Checkbox';
import LoadingSpinner from '../../components/shared/LoadingSpinner';
import FeedbackModal from '../../components/shared/ModalNotification';
import {
  FormContainer, FormSection, FormHeader, FormGrid, FormField, SubmitButton,
} from '../../components/styled/FormComponents';
import { getTimeTrackingPrefs, saveTimeTrackingPrefs } from './timeTrackingService';

const TimeTrackingSettings = () => {
  const [prefs, setPrefs] = useState({ id: null, default_radius_feet: 600, auto_create_site: false });
  const [initialLoading, setInitialLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [errors, setErrors] = useState({});
  const [showFeedback, setShowFeedback] = useState(false);
  const [feedbackMessage, setFeedbackMessage] = useState('');
  const [feedbackSuccess, setFeedbackSuccess] = useState(true);

  useEffect(() => {
    getTimeTrackingPrefs()
      .then(setPrefs)
      .catch(() => {})
      .finally(() => setInitialLoading(false));
  }, []);

  const handleSubmit = async (e) => {
    e.preventDefault();
    const radius = parseInt(prefs.default_radius_feet, 10);
    if (!Number.isFinite(radius) || radius <= 0) {
      setErrors({ default_radius_feet: 'Enter a radius greater than 0' });
      return;
    }
    setSaving(true);
    try {
      await saveTimeTrackingPrefs(prefs);
      // Re-read so we capture the new record id after a first-time create.
      const fresh = await getTimeTrackingPrefs();
      setPrefs(fresh);
      setFeedbackMessage('Settings saved'); setFeedbackSuccess(true);
    } catch (err) {
      setFeedbackMessage(err.response?.data?.message || 'Failed to save settings'); setFeedbackSuccess(false);
    } finally {
      setSaving(false); setShowFeedback(true);
    }
  };

  if (initialLoading) return <DashboardLayout><LoadingSpinner /></DashboardLayout>;

  return (
    <DashboardLayout>
      <FormContainer>
        <FormHeader>
          <h2><FaCog style={{ marginRight: 8 }} />Time Tracking Settings</h2>
        </FormHeader>

        <form onSubmit={handleSubmit}>
          <FormSection>
            <h3>Defaults</h3>
            <FormGrid>
              <FormField index={0}>
                <Input
                  label="Default geofence radius (feet)" icon={<FaRulerHorizontal />}
                  type="number" min="1" value={prefs.default_radius_feet}
                  onChange={(e) => setPrefs((s) => ({ ...s, default_radius_feet: e.target.value }))}
                  error={errors.default_radius_feet}
                />
              </FormField>
            </FormGrid>
            <div style={{ marginTop: 12 }}>
              <Checkbox
                label='Check "Add as clock-in site" by default when creating a job'
                checked={!!prefs.auto_create_site}
                onChange={(e) => setPrefs((s) => ({ ...s, auto_create_site: e.target.checked }))}
              />
            </div>
          </FormSection>

          <SubmitButton type="submit" disabled={saving}>
            {saving ? (<><FaSpinner className="icon-spin" />Saving...</>) : (<><FaSave />Save Settings</>)}
          </SubmitButton>
        </form>
      </FormContainer>

      <FeedbackModal isOpen={showFeedback} message={feedbackMessage}
        success={feedbackSuccess} onComplete={() => setShowFeedback(false)} />
    </DashboardLayout>
  );
};

export default TimeTrackingSettings;
